import React, { FC, ReactNode } from 'react';
import cx from 'classnames';
import sum from 'lodash/sum';

import { config } from 'data';
import { formatter } from 'helpers';
import { useLang } from 'hooks';
import { ArrowDown, ArrowUp } from 'components/icons';
import { Highcharts } from 'components/layout';
import { Card, Flex, Spin, Text, Title } from 'components/ui';
import { Override } from 'types/common';
import { CardProps } from 'types/components';

import styles from './styles.module.css';

import Gradients from './Gradients';

type CardViewProps = Override<CardProps, {
  title: ReactNode;
  amounts: Array<{
    date: string;
    value: number;
  }>;
  amount?: number;
  loading?: boolean;
  positive?: boolean;
  primary?: boolean;
}>;

const calculateChange = (values: number[]) => {
  const firstValue = values[0] ?? 0;
  const lastValue = values[values.length - 1] ?? 0;

  if (firstValue === 0) {
    return lastValue === 0 ? 0 : 100;
  }

  return Math.ceil((lastValue - firstValue) / firstValue * 100);
};

const CardView: FC<CardViewProps> = ({
  className,
  title,
  amounts,
  amount,
  loading,
  positive = true,
  primary,
  ...props
}) => {
  const lang = useLang();

  const values = amounts.map((amount) => formatter.transformFromCents(amount.value));
  const change = calculateChange(values);
  const increasing = change >= 0;

  const formattedValue = formatter.formatNumber(Math.abs(amount ?? sum(values)), 0);
  const formattedChange = formatter.formatPercent(Math.abs(change), 0);

  return (
    <Card
      className={cx(
        styles.root,
        positive ? styles.root_positive : styles.root_negative,
        className,
      )}
      {...props}
    >

      <Title className={styles.title} level={5}>{title}, {config.DEFAULT_CURRENCY}</Title>

      <Flex gap="middle" align="flex-end" justify="space-between">
        <Flex className={styles.content} gap="middle" vertical>
          <div className={cx(styles.value, primary && styles.value_primary)}>
            {loading ? <Spin /> : formattedValue}
          </div>
          <div className={styles.caption}>
            <div className={cx(
              styles.changes,
              increasing ? styles.changes_increasing : styles.changes_decreasing,
            )}>
              {increasing ? <ArrowUp /> : <ArrowDown />}
              {formattedChange}
            </div>
            <Text type="secondary">{lang.get('dashboard.card.changes')}</Text>
          </div>
        </Flex>

        {!loading && (
          <Highcharts
            className={styles.chart}
            options={{
              plotOptions: {
                series: {
                  marker: {
                    enabled: false,
                  },
                },
              },
              xAxis: {
                visible: false,
              },
              yAxis: {
                visible: false,
                softMin: -0.01,
                softMax: 0.01,
              },
              series: [
                {
                  type: 'areaspline',
                  data: values,
                },
              ],
            }}
          />
        )}

      </Flex>
      <Gradients />
    </Card>
  );
};

export default CardView;
